/**
 * 振り返り画面 / 申請書画面 / PDF出力 で共通利用する Chart.js プラグイン。
 *
 * 「申請時計画値（datasets[0]）」と「実績（datasets[1]）/今回見通し（datasets[2]）」の差分エリアを
 * 緑（実績/見通しが計画より大きい）または赤（計画より小さい）で塗りつぶす描画ロジック。
 *
 * ReviewBusiness 画面の `lifecycleFill` / `cumulativeFill` プラグインを共通化したもの。
 * 振り返り境界 `boundaryIdx` を引数で受け取り、境界の前後で「実績」と「見通し」を自動切替する。
 */

/**
 * 生産数量（lifecycle）チャート用プラグイン。
 * 各セグメントごとに、申請時計画値（datasets[0]）と実績/見通し（datasets[1]/datasets[2]）の間の
 * 4点ベジェ閉曲線を作り、平均値の大小で色を切り替えて塗りつぶす。
 */
export const buildLifecycleFillPlugin = (boundaryIdx: number) => ({
  id: "reviewLifecycleFill",
  beforeDatasetsDraw(chart: any) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const compareMeta = chart.getDatasetMeta(0)?.data;
    const m1 = chart.getDatasetMeta(1)?.data;
    const m2 = chart.getDatasetMeta(2)?.data;
    if (!compareMeta || compareMeta.length < 2) return;

    const ds0: number[] = chart.data?.datasets?.[0]?.data ?? [];
    const ds1: (number | null)[] = chart.data?.datasets?.[1]?.data ?? [];
    const ds2: (number | null)[] = chart.data?.datasets?.[2]?.data ?? [];
    const getActualVal = (idx: number): number | null => {
      const fromMain = idx < boundaryIdx ? ds1[idx] : ds2[idx];
      if (fromMain !== null && fromMain !== undefined) return fromMain;
      const alt = idx < boundaryIdx ? ds2[idx] : ds1[idx];
      return alt ?? null;
    };
    const getActualPt = (idx: number) => (idx < boundaryIdx ? m1?.[idx] ?? m2?.[idx] : m2?.[idx] ?? m1?.[idx]);
    const ox = (pt: any) => pt.cp2x ?? pt.x; const oy = (pt: any) => pt.cp2y ?? pt.y;
    const ix = (pt: any) => pt.cp1x ?? pt.x; const iy = (pt: any) => pt.cp1y ?? pt.y;

    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();

    const n = compareMeta.length;
    for (let i = 0; i < n - 1; i++) {
      const c0 = compareMeta[i], c1 = compareMeta[i + 1];
      const a0 = getActualPt(i), a1 = getActualPt(i + 1);
      const av0 = getActualVal(i), av1 = getActualVal(i + 1);
      const cv0 = ds0[i], cv1 = ds0[i + 1];
      if (!c0 || !c1 || !a0 || !a1) continue;
      if (av0 == null || av1 == null || cv0 == null || cv1 == null) continue;

      const avgA = (cv0 + cv1) / 2;
      const avgB = (av0 + av1) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(c0.x, c0.y);
      ctx.bezierCurveTo(ox(c0), oy(c0), ix(c1), iy(c1), c1.x, c1.y);
      ctx.lineTo(a1.x, a1.y);
      ctx.bezierCurveTo(ix(a1), iy(a1), ox(a0), oy(a0), a0.x, a0.y);
      ctx.closePath();
      ctx.fillStyle = avgA > avgB ? "rgba(229, 62, 62, 0.15)" : "rgba(56, 161, 105, 0.15)";
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();
  },
});

/**
 * 累積生産数量チャート用プラグイン。
 * 各セグメントごとに、申請時計画値と累積実績/見通しの差分エリアを四辺形で塗りつぶし、
 * 差分の符号がセグメント途中で反転する場合はクロスポイントで色を切り替える。
 */
export const buildCumulativeFillPlugin = (boundaryIdx: number) => ({
  id: "reviewCumulativeFill",
  beforeDatasetsDraw(chart: any) {
    const { ctx, scales } = chart;
    const xScale = scales?.x; const yScale = scales?.y;
    if (!xScale || !yScale) return;

    const ds0: number[] = chart.data?.datasets?.[0]?.data ?? [];
    const ds1: (number | null)[] = chart.data?.datasets?.[1]?.data ?? [];
    const ds2: (number | null)[] = chart.data?.datasets?.[2]?.data ?? [];
    const getActualVal = (idx: number): number | null => {
      const fromMain = idx < boundaryIdx ? ds1[idx] : ds2[idx];
      if (fromMain !== null && fromMain !== undefined) return fromMain;
      const alt = idx < boundaryIdx ? ds2[idx] : ds1[idx];
      return alt ?? null;
    };

    ctx.save();
    const n = ds0.length;
    for (let i = 0; i < n - 1; i++) {
      const c1 = ds0[i], c2 = ds0[i + 1];
      const a1 = getActualVal(i), a2 = getActualVal(i + 1);
      if (c1 == null || c2 == null || a1 == null || a2 == null) continue;
      const diff1 = a1 - c1, diff2 = a2 - c2;
      const effectiveDiff1 = diff1 === 0 ? diff2 : diff1;
      if (Math.abs(diff1) < 1 && Math.abs(diff2) < 1) continue;
      const x1 = xScale.getPixelForValue(i);
      const x2 = xScale.getPixelForValue(i + 1);

      ctx.save();
      if (effectiveDiff1 * diff2 < 0) {
        const tRatio = effectiveDiff1 / (effectiveDiff1 - diff2);
        const xCross = x1 + (x2 - x1) * tRatio;
        const crossVal = c1 + (c2 - c1) * tRatio;
        const yCross = yScale.getPixelForValue(crossVal);
        ctx.beginPath();
        ctx.moveTo(x1, yScale.getPixelForValue(c1));
        ctx.lineTo(xCross, yCross);
        ctx.lineTo(x1, yScale.getPixelForValue(a1));
        ctx.closePath();
        ctx.fillStyle = effectiveDiff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)";
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(xCross, yCross);
        ctx.lineTo(x2, yScale.getPixelForValue(c2));
        ctx.lineTo(x2, yScale.getPixelForValue(a2));
        ctx.closePath();
        ctx.fillStyle = diff2 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)";
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(x1, yScale.getPixelForValue(c1));
        ctx.lineTo(x2, yScale.getPixelForValue(c2));
        ctx.lineTo(x2, yScale.getPixelForValue(a2));
        ctx.lineTo(x1, yScale.getPixelForValue(a1));
        ctx.closePath();
        ctx.fillStyle = effectiveDiff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)";
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.restore();
  },
});
